import React from 'react';
import { Grid, Paper, Typography, Box } from '@material-ui/core';
import type { BirdsEyeViewAnalytics } from '../../../../_models/schedule.model';

interface KPICardsProps {
  analytics: BirdsEyeViewAnalytics;
}

interface KPICardProps {
  title: string;
  value: string | number;
  icon: string;
  color: string;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, color, subtitle }) => {
  return (
    <Paper
      elevation={3}
      style={{
        padding: '24px',
        borderRadius: '16px',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `2px solid ${color}30`,
        height: '100%',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box flex={1}>
          <Typography
            variant="body2"
            style={{
              color: 'var(--text-muted)',
              fontWeight: 600,
              fontSize: '13px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '8px',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h3"
            style={{
              fontWeight: 700,
              color: 'var(--text-strong)',
              fontSize: '36px',
              marginBottom: subtitle ? '4px' : '0',
            }}
          >
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          style={{
            fontSize: '48px',
            opacity: 0.8,
            marginLeft: '16px',
          }}
        >
          {icon}
        </Box>
      </Box>
    </Paper>
  );
};

const KPICards: React.FC<KPICardsProps> = ({ analytics }) => {
  const calculateSessionCompletionRate = (): string => {
    const conducted = analytics.statusCounts.conducted;
    const total = analytics.totalSlots;
    if (total === 0) return '0%';
    return ((conducted / total) * 100).toFixed(1) + '%';
  };

  const calculateSessionCancellationRate = (): string => {
    const cancelled = analytics.statusCounts.cancelled;
    const total = analytics.totalSlots;
    if (total === 0) return '0%';
    return ((cancelled / total) * 100).toFixed(1) + '%';
  };

  const kpiData = [
    {
      title: 'Total Sessions',
      value: analytics.totalSlots.toLocaleString(),
      icon: '📚',
      color: 'var(--brand-primary)',
      subtitle: 'All scheduled activities',
    },
    {
      title: 'Teaching Hours',
      value: `${analytics.totalHours.toLocaleString()}h`,
      icon: '⏱️',
      color: '#f6ad55',
      subtitle: 'Total instruction time',
    },
    {
      title: 'Active Instructors',
      value: analytics.totalInstructors.toLocaleString(),
      icon: '👨‍🏫',
      color: '#48bb78',
      subtitle: `${(analytics.totalHours / (analytics.totalInstructors || 1)).toFixed(1)}h avg per instructor`,
    },
    {
      title: 'Rooms Used',
      value: analytics.totalRooms.toLocaleString(),
      icon: '🏫',
      color: '#4299e1',
      subtitle: `${(analytics.totalSlots / (analytics.totalRooms || 1)).toFixed(1)} sessions per room`,
    },
    {
      title: 'Session Completion',
      value: calculateSessionCompletionRate(),
      icon: '✅',
      color: '#38b2ac',
      subtitle: `${analytics.statusCounts.conducted} of ${analytics.totalSlots} conducted`,
    },
    {
      title: 'Session Cancellation',
      value: calculateSessionCancellationRate(),
      icon: '❌',
      color: '#f56565',
      subtitle: `${analytics.statusCounts.cancelled} of ${analytics.totalSlots} cancelled`,
    },
    // Capacity-based metrics (only show if available)
    ...(analytics.capacityMetrics ? [
      {
        title: 'Room Utilization',
        value: `${analytics.capacityMetrics.rooms.utilizationRate}%`,
        icon: '🏢',
        color: '#9f7aea',
        subtitle: `${analytics.capacityMetrics.rooms.totalScheduledHours}h / ${analytics.capacityMetrics.rooms.totalAvailableHours}h capacity`,
      },
      {
        title: 'Instructor Load',
        value: `${analytics.capacityMetrics.instructors.utilizationRate}%`,
        icon: '👔',
        color: '#ed64a6',
        subtitle: `${analytics.capacityMetrics.instructors.totalTeachingHours}h / ${analytics.capacityMetrics.instructors.totalAvailableHours}h capacity`,
      },
    ] : []),
  ];

  return (
    <Grid container spacing={3}>
      {kpiData.map((kpi, index) => (
        <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
          <KPICard
            title={kpi.title}
            value={kpi.value}
            icon={kpi.icon}
            color={kpi.color}
            subtitle={kpi.subtitle}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default KPICards;
